/**
 * Is the cloud reachable? (06-flows.md F10 step 1: "failed heartbeat + navigator.onLine".)
 *
 * The state is `offline` when the browser says so, or when a heartbeat (or any API call)
 * gets no answer from the server; it comes back to `online` on the browser's `online`
 * event or the next heartbeat that answers. Any HTTP answer below 500 proves the server
 * is there; the body does not matter. The sync runner and the banner read this; nothing
 * else decides whether to queue a write (that is `dispatch`).
 */
import { useSyncExternalStore } from 'react';

export type Connectivity = 'online' | 'offline';

type Listener = () => void;

const HEARTBEAT_ONLINE_MS = 10_000;
const HEARTBEAT_OFFLINE_MS = 3_000;
const HEARTBEAT_TIMEOUT_MS = 4_000;
/** A fetch that answers is proof of life; a probe is only needed after this quiet time. */

let state: Connectivity =
  typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online';
const listeners = new Set<Listener>();
let timer: ReturnType<typeof setTimeout> | undefined;
let running = false;
let probeUrl = '/health/live';
let probeFetch: (url: string, init: RequestInit) => Promise<Response> = (url, init) =>
  fetch(url, init);

function set(next: Connectivity): void {
  if (next === state) return;
  state = next;
  listeners.forEach((l) => l());
}

export const connectivity = {
  get: (): Connectivity => state,
  isOnline: (): boolean => state === 'online',
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** An API call got no answer (the network is down, or the server is). */
  reportFailure(): void {
    set('offline');
    schedule();
  },
  /** An API call was answered. */
  reportAnswer(): void {
    set('online');
  },
  /** Tests: set the state without probing. */
  force(next: Connectivity): void {
    set(next);
  },
};

export function useConnectivity(): Connectivity {
  return useSyncExternalStore(connectivity.subscribe, connectivity.get, connectivity.get);
}

/** One heartbeat: resolves to whether the server answered. */
export async function probe(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    set('offline');
    return false;
  }
  const controller = new AbortController();
  const abort = setTimeout(() => controller.abort(), HEARTBEAT_TIMEOUT_MS);
  try {
    const res = await probeFetch(probeUrl, { cache: 'no-store', signal: controller.signal });
    const up = res.status < 500;
    set(up ? 'online' : 'offline');
    return up;
  } catch {
    set('offline');
    return false;
  } finally {
    clearTimeout(abort);
  }
}

function schedule(): void {
  if (!running) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(
    () => {
      void probe().finally(schedule);
    },
    state === 'online' ? HEARTBEAT_ONLINE_MS : HEARTBEAT_OFFLINE_MS,
  );
}

/** Start listening to the browser and heartbeating. Returns the stop function. */
export function startConnectivity(
  options: {
    url?: string;
    fetch?: (url: string, init: RequestInit) => Promise<Response>;
  } = {},
): () => void {
  if (options.url) probeUrl = options.url;
  if (options.fetch) probeFetch = options.fetch;
  running = true;
  const onOnline = () => void probe().finally(schedule);
  const onOffline = () => {
    set('offline');
    schedule();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
  }
  void probe().finally(schedule);
  return () => {
    running = false;
    if (timer) clearTimeout(timer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    }
  };
}
